import styled from '@emotion/styled';
import { colors, typography } from '@/styles';

export const GridCard = styled.div`
    background-color: ${colors.white.primary};
    border: 1px solid ${colors.greys.border};
    border-radius: 8px;
    overflow: hidden;
`;

export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 12px 18px;
    border-bottom: 1px solid ${colors.greys.border};
`;

export const LegendRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 9px 18px;
    border-bottom: 1px solid ${colors.greys.border};
`;

export const LegendLabel = styled.span`
    ${typography.sansSerif}
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${colors.greys.subtitle};
`;

export const LegendItem = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    ${typography.sansSerif}
    font-size: 11px;
    font-weight: 500;
    color: ${colors.greys.secondary};
`;

export const LegendDot = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background-color: ${(props) => props.color};
`;

export const HeadRow = styled.div`
    display: grid;
    grid-template-columns: ${(props) => props.columns};
    gap: 8px;
    align-items: center;
    padding: 10px 18px;
    background-color: ${colors.white.secondary};
    border-bottom: 1px solid ${colors.greys.border};
`;

export const HeadCell = styled.span`
    ${typography.sansSerif}
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${(props) => (props.active ? colors.greens.primary : colors.greys.primary)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// Header cell wrapper: label + sort/filter indicators + kebab + resize handle.
export const HeadCellWrap = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 2px;
    min-width: 0;
`;

export const ResizeHandle = styled.span`
    position: absolute;
    top: -8px;
    right: -8px;
    width: 9px;
    height: calc(100% + 16px);
    cursor: col-resize;
    user-select: none;
    touch-action: none;

    &:hover::after,
    &:active::after {
        content: '';
        position: absolute;
        top: 0;
        right: 4px;
        width: 2px;
        height: 100%;
        background-color: ${colors.greens.primary};
    }
`;

export const LineRow = styled.div`
    display: grid;
    grid-template-columns: ${(props) => props.columns};
    gap: 8px;
    align-items: center;
    padding: 12px 18px;
    border-top: 1px solid ${colors.greys.border};
`;

export const SkuCell = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
`;

export const Chevron = styled.span`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    cursor: pointer;
    color: ${colors.greys.primary};
    font-size: 10px;
    transition: transform 0.15s;
    transform: rotate(${(props) => (props.open ? '90deg' : '0deg')});
`;

export const MonoText = styled.span`
    ${typography.sansSerif}
    font-size: 11.5px;
    font-weight: 500;
    color: ${colors.greys.secondary};
    flex: none;
`;

export const NameText = styled.span`
    ${typography.sansSerif}
    font-size: 13px;
    font-weight: 700;
    color: ${colors.greys[3]};
    flex: none;
`;

export const DescText = styled.span`
    ${typography.sansSerif}
    font-size: 11.5px;
    font-weight: 500;
    color: ${colors.greys.subtitle};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const QtyText = styled.span`
    ${typography.sansSerif}
    font-size: 12px;
    font-weight: 700;
    color: ${colors.greys[3]};
`;

export const StatusBar = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
`;

export const SegTrack = styled.div`
    flex: 1;
    display: flex;
    height: 8px;
    border-radius: 5px;
    overflow: hidden;
    background-color: ${colors.greys.border};
`;

export const Seg = styled.div`
    height: 100%;
    width: ${(props) => props.percent}%;
    background-color: ${(props) => props.color};
`;

export const SegText = styled.span`
    ${typography.sansSerif}
    font-size: 10px;
    font-weight: 500;
    color: ${colors.greys.secondary};
    white-space: nowrap;
`;

// em-based min-width so every status pill in a column renders the same width; override per grid.
export const Pill = styled.span`
    ${typography.sansSerif}
    justify-self: start;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
    padding: 4px 10px;
    border-radius: 999px;
    min-width: ${(props) => props.minWidth || '9.5em'};
    text-align: center;
    color: ${(props) => props.color};
    background-color: ${(props) => `${props.color}1F`};
    border: 1px solid ${(props) => `${props.color}52`};
`;

export const PalletText = styled.span`
    ${typography.sansSerif}
    font-size: 11.5px;
    font-weight: 500;
    color: ${(props) => (props.muted ? colors.greys.subtitle : colors.greys.secondary)};
`;

export const IdLink = styled.span`
    ${typography.sansSerif}
    font-size: 11.5px;
    font-weight: 600;
    color: ${colors.blues.primary};
`;

// Expanded child area
export const ChildPanel = styled.div`
    padding: 6px 20px 16px 44px;
    background-color: ${colors.white.secondary};
`;

export const ChildDivider = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 10px 0;

    & > span:last-child {
        flex: 1;
        height: 1px;
        background-color: ${colors.greys.border};
    }
`;

export const ChildDividerLabel = styled.span`
    ${typography.sansSerif}
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.greys.primary};
`;

export const ChildTable = styled.div`
    border: 1px solid ${colors.greys.border};
    border-radius: 8px;
    overflow: hidden;
    background-color: ${colors.white.primary};
`;

export const ChildHeadRow = styled.div`
    display: grid;
    grid-template-columns: ${(props) => props.columns};
    gap: 8px;
    align-items: center;
    padding: 9px 14px;
    background-color: ${colors.white.secondary};
`;

export const ChildRow = styled.div`
    display: grid;
    grid-template-columns: ${(props) => props.columns};
    gap: 8px;
    align-items: center;
    padding: 9px 14px;
    border-top: 1px solid ${colors.greys.border};
`;

export const ChildCell = styled.span`
    ${typography.sansSerif}
    font-size: 11.5px;
    font-weight: 500;
    color: ${colors.greys.secondary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Footnote = styled.div`
    ${typography.sansSerif}
    font-size: 11px;
    line-height: 1.55;
    color: ${colors.greys.subtitle};
    margin-top: 9px;
    max-width: 640px;
`;

export const EmptyState = styled.div`
    ${typography.sansSerif}
    padding: 3rem;
    text-align: center;
    font-size: 13px;
    color: ${colors.greys.secondary};
`;

export const SelectCell = styled.div`
    display: flex;
    align-items: center;
`;

export const KebabCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

export const BulkBar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 11px 16px;
    background-color: ${colors.white.primary};
    border: 1px solid ${colors.greys.border};
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
`;

// Sticky footer version for the parent (page-level) bulk bar.
export const StickyBulkBar = styled(BulkBar)`
    position: sticky;
    bottom: 16px;
    margin-top: 14px;
    z-index: 3;
`;

// Inline version shown inside an expanded child panel.
export const InlineBulkBar = styled(BulkBar)`
    margin-bottom: 10px;
    box-shadow: none;
`;

export const BulkCount = styled.span`
    ${typography.sansSerif}
    font-size: 12.5px;
    font-weight: 600;
    color: ${colors.greys[3]};

    & b {
        font-weight: 800;
    }
`;

export const BulkDivider = styled.span`
    width: 1px;
    height: 18px;
    background-color: ${colors.greys.border};
`;

export const BulkSpacer = styled.div`
    flex: 1;
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const bulkButtonBase = `
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 13px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
`;

export const BulkButtonPrimary = styled.button`
    ${typography.sansSerif}
    ${bulkButtonBase}
    border: none;
    color: ${colors.white.primary};
    background-color: ${colors.greens.primary};

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

export const BulkButtonOutlined = styled.button`
    ${typography.sansSerif}
    ${bulkButtonBase}
    background-color: ${colors.white.primary};
    color: ${(props) => props.color || colors.greys.secondary};
    border: 1px solid ${(props) => `${props.color || colors.greys.primary}66`};

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

export const ClearButton = styled.button`
    ${typography.sansSerif}
    padding: 8px 11px;
    border-radius: 8px;
    border: none;
    background-color: transparent;
    color: ${colors.greys.primary};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

// ---- Header blocks (moved from Manifest/blocks.js; generic display primitives) ----

export const FieldLabel = styled.span`
    ${typography.sansSerif}
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.greys.subtitle};
`;

export const FieldValue = styled.span`
    ${typography.sansSerif}
    font-size: 14px;
    font-weight: 700;
    color: ${colors.greys[3]};
`;

export const StatusPill = styled.span`
    ${typography.sansSerif}
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${(props) => props.color};
    background-color: ${(props) => `${props.color}1A`};
    border: 1px solid ${(props) => `${props.color}66`};
    border-radius: 999px;
    padding: 4px 12px;
    white-space: nowrap;
`;

export const ProgressTrack = styled.div`
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background-color: ${colors.greys.border};
    overflow: hidden;
`;

export const ProgressFill = styled.div`
    width: ${(props) => Math.min(100, Math.max(0, props.percent))}%;
    height: 100%;
    border-radius: 4px;
    background-color: ${colors.greens.primary};
`;
